import { ConfirmReserveModal, EventModal } from '_/components';
import ExclusiveSlotModal from '_/components/ExclusiveSlotModal';
import { colors } from '_/constants/theme';
import { useAuth } from '_/hooks/AuthContext';
import { useEventContext } from '_/hooks/EventContext';
import { useSlotContext } from '_/hooks/SlotContext';
import { useSpaceContext } from '_/hooks/SpaceContext';
import useUpdateSlotCoordinates from '_/hooks/useUpdateSlotCoordinates';
import { SlotProps } from '_/screens/Workstation/StatusTab/AvailableList/Slot';
import { EventStatus } from '_/services/models/enums/event-status.enum';
import { RecurrenceType } from '_/services/models/enums/recurrence-type.enum';
import { EventsModel } from '_/services/models/events.model';
import { MembersModel } from '_/services/models/members.model';
import { SlotsModel } from '_/services/models/slots.model';
import L from 'leaflet';
import React, { useCallback, useMemo, useState } from 'react';
import { Circle, LayerGroup, Marker, useMap, useMapEvent } from 'react-leaflet';

interface CustomMarksProps extends SlotProps {
  eventModalVisible: string | undefined;
  setEventModalVisible: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const MapMarker: React.FC<CustomMarksProps> = ({
  slot,
  eventModalVisible,
  setEventModalVisible,
}) => {
  const { currentSpace } = useSpaceContext();
  const { setIsSlotModalVisible, isSlotModalVisible } = useSlotContext();
  const { updateSlot } = useUpdateSlotCoordinates();
  const { member } = useAuth();
  const { setRecurrenceStr } = useEventContext();
  const [exclusiveSlotModalVisible, setExclusiveSlotModalVisible] = useState<string>('');
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(RecurrenceType.ONCE);
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());

  useMapEvent('zoomend', () => {
    setZoom(map.getZoom());
  });

  const events = useMemo(() => {
    return slot.events?.filter((event) => event.status !== EventStatus.CHECKEDOUT) || [];
  }, [slot.events]);

  const mapClick = useMapEvent('click', (e) => {
    const { lat, lng } = e.latlng;
    const coordinates = {
      latitude: lat,
      longitude: lng,
    };

    updateSlot(coordinates);
  });

  const handleConfirmReserve = useCallback(
    (slot: Omit<SlotsModel, 'amenities'>) => {
      setRecurrenceStr('');
      setIsSlotModalVisible(slot.id);
    },
    [setIsSlotModalVisible, setRecurrenceStr]
  );

  const handlePressMarker = useCallback(() => {
    mapClick.setView([slot.mapLocation?.latitude, slot.mapLocation?.longitude]);

    if (slot.exclusiveForMemberId && member?.id !== slot.exclusiveForMemberId)
      return setExclusiveSlotModalVisible(slot.exclusiveForMemberId);

    const events = slot.events?.filter((event) => event.status !== EventStatus.CHECKEDOUT) || [];

    if (events.length === 0) {
      handleConfirmReserve(slot);
    } else {
      setEventModalVisible(slot?.events?.[0].slotId);
    }
  }, [handleConfirmReserve, mapClick, setEventModalVisible, slot, member]);

  const HandleEventModal = useCallback(() => {
    if (events[0]) {
      return (
        <EventModal
          eventModalVisible={eventModalVisible}
          onBackdropPress={() => setEventModalVisible(undefined)}
          onBackButtonPress={() => setEventModalVisible(undefined)}
          onSwipeStart={() => setEventModalVisible(undefined)}
          id={events[0]?.slotId}
          name={events[0]?.user?.name as string}
          source={events[0].user?.pictureThumbUrl as string}
          startDate={events[0]?.startAt as unknown as string}
          endDate={events[0]?.endAt as unknown as string}
        />
      );
    }
    if (exclusiveSlotModalVisible) {
      return (
        <ExclusiveSlotModal
          slotId={slot.id}
          exclusiveMemberId={exclusiveSlotModalVisible}
          onBackdropPress={() => setExclusiveSlotModalVisible('')}
          onBackButtonPress={() => setExclusiveSlotModalVisible('')}
          onSwipeStart={() => setExclusiveSlotModalVisible('')}
        />
      );
    }
    return null;
  }, [
    eventModalVisible,
    setEventModalVisible,
    events,
    exclusiveSlotModalVisible,
    setExclusiveSlotModalVisible,
    slot.id,
  ]);

  const slotRadius = useMemo(() => {
    if (currentSpace.mapData?.slotRadiusWeb) {
      return currentSpace.mapData.slotRadiusWeb;
    }

    if (currentSpace.mapData?.slotRadius) {
      return currentSpace.mapData?.slotRadius * 0.0000075;
    }

    return 0.07;
  }, [currentSpace.mapData?.slotRadius, currentSpace.mapData?.slotRadiusWeb]);

  if (!slot.mapLocation?.latitude || !slot.mapLocation?.longitude) {
    return null;
  }

  const colorCircle = (slot: SlotsModel, events: EventsModel[], member: MembersModel) => {
    if (events.length > 0) return colors.errorRed;
    if (slot.exclusiveForMemberId && member.id !== slot.exclusiveForMemberId) return colors.grey;
    return colors.successGreen;
  };

  const isOwnExclusiveSlot = member?.id === slot.exclusiveForMemberId;
  const exclusiveUserIcon = useMemo(() => {
    if (!isOwnExclusiveSlot || !slot.mapLocation?.latitude || !slot.mapLocation?.longitude)
      return null;

    const center = L.latLng(slot.mapLocation.latitude, slot.mapLocation.longitude);
    const edge = L.latLng(slot.mapLocation.latitude + slotRadius, slot.mapLocation.longitude);
    const centerPx = map.latLngToLayerPoint(center);
    const edgePx = map.latLngToLayerPoint(edge);
    const pixelRadius = Math.abs(centerPx.y - edgePx.y);
    const iconSize = pixelRadius * 1.5;
    const iconAnchor = iconSize / 2;

    return L.divIcon({
      className: '',
      html: `
        <div style="
          width: ${iconSize}px;
          height: ${iconSize}px;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        ">
          <svg 
            viewBox="0 0 24.00 24.00" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="#ffffff" 
            stroke="#ffffff" 
            stroke-width="0.00024000000000000003">
            <g 
              id="SVGRepo_bgCarrier" 
              stroke-width="0">
            </g>
              <g 
                id="SVGRepo_tracerCarrier" 
                stroke-linecap="round" 
                stroke-linejoin="round">
              </g>
              <g id="SVGRepo_iconCarrier">
                <g> 
                  <path 
                    fill="none" 
                    d="M0 0h24v24H0z">
                  </path> 
                  <path d="M12 14v2a6 6 0 0 0-6 6H4a8 8 0 0 1 8-8zm0-1c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm0-2c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm6 10.5l-2.939 1.545.561-3.272-2.377-2.318 3.286-.478L18 14l1.47 2.977 3.285.478-2.377 2.318.56 3.272L18 21.5z">
                  </path> 
                </g> 
            </g>
          </svg>
        </div>
      `,
      iconSize: [iconSize, iconSize],
      iconAnchor: [iconAnchor, iconAnchor],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnExclusiveSlot, slotRadius, zoom, map, slot.mapLocation]);

  return (
    <LayerGroup>
      <Circle
        pathOptions={{
          fillColor: colorCircle(slot as SlotsModel, events, member as MembersModel),
          color: colorCircle(slot as SlotsModel, events, member as MembersModel),
        }}
        radius={slotRadius}
        center={{ lat: slot.mapLocation.latitude, lng: slot.mapLocation.longitude }}
        stroke={false}
        opacity={1}
        fillOpacity={1}
        eventHandlers={{
          click: handlePressMarker,
        }}
      />

      {exclusiveUserIcon && (
        <Marker
          position={[slot.mapLocation.latitude, slot.mapLocation.longitude]}
          icon={exclusiveUserIcon}
          zIndexOffset={1000}
          eventHandlers={{
            click: handlePressMarker,
          }}
        />
      )}

      {isSlotModalVisible === slot.id && (
        <ConfirmReserveModal
          onBackdropPress={() => setIsSlotModalVisible('')}
          onBackButtonPress={() => setIsSlotModalVisible('')}
          onSwipeStart={() => setIsSlotModalVisible('')}
          slot={slot}
          recurrenceType={recurrenceType}
          setRecurrenceType={setRecurrenceType}
        />
      )}

      <HandleEventModal />
    </LayerGroup>
  );
};

export default MapMarker;
